import React, { Component } from 'react';
import cn from 'classnames';
import find from 'lodash/find';
import { isWithinElement, mergeClassNames, prefixPrevProps } from '../../utils/utilsGlobal';
import originalStyles from './Dropdown.scss';

export class Dropdown extends Component {

  focusableButtonChildren = null;
  focusedDropdownItem = null;

  container = React.createRef();
  dropdown = React.createRef();
  button = React.createRef();

  state = {
    isOpen: false,
    isFocused: false,
    selectedItemValue: null,
    focusedItemValue: null,
    isContainerFocusable: true,
  };

  componentDidMount(){
    const { defaultValue } = this.props;
    this.setState({ selectedItemValue: defaultValue });

    this.focusableButtonChildren = this.button.current.querySelector('input, button');
    if (this.focusableButtonChildren) {
      this.setState({ isContainerFocusable: false });
    }
    document.body.addEventListener('click', this.onBodyClick);
    document.body.addEventListener('touchend', this.onBodyClick);
  }

  componentDidUpdate(prevProps, prevState){
    const { items, defaultValue, onClose } = this.props;
    const { prevItems, prevDefaultValue } = prefixPrevProps(prevProps);

    const didDropdownOpen = this.state.isOpen && !prevState.isOpen;
    const didDropdownClose = !this.state.isOpen && prevState.isOpen;

    if (defaultValue !== prevDefaultValue) {
      this.setState({ selectedItemValue: defaultValue });
    }
    if (didDropdownOpen || items !== prevItems) {
      this.resizeDropdownToFitViewport();
    }
    if (didDropdownClose && onClose) {
      onClose();
    }
  }

  componentWillUnmount(){
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.removeEventListener('touchend', this.onBodyClick);
  }

  onBodyClick = (e) => {
    const { dontKeepSelectedOption } = this.props;
    if (!isWithinElement(e.target, this.container.current)) {
      this.setState({
        isOpen: false,
        isFocused: false,
        focusedItemValue: null,
      });
    }
    if (dontKeepSelectedOption) {
      this.setState({ selectedItemValue: null });
    }
  };

  onComponentFocus = () => {
    if (!this.state.isFocused) {
      this.setState({ isFocused: true });
    }
  };

  onComponentBlur = (e) => {
    if (!isWithinElement(e.relatedTarget, this.container.current)) {
      this.setState({
        isFocused: false,
        isOpen: false,
      });
    }
  };

  setFocusOnButton = () => {
    if (this.focusableButtonChildren) {
      this.focusableButtonChildren.focus();
    }
    else {
      this.container.current.focus();
    }
  };

  openDropdown = () => {
    this.setState({ isOpen: true });
  };

  manuallyCloseDropdown = () => {
    const { dontKeepSelectedOption } = this.props;
    this.setState({ isOpen: false });
    if (dontKeepSelectedOption) {
      this.setState({ selectedItemValue: null });
    }
  };

  onComponentKeydown = (e) => {
    const { items } = this.props;
    if (items && items.length) {
      if (e.which === 13) { // Enter
        this.onEnterPress();
      }
      else if (e.which === 38) { // Arrow up
        this.onArrowKeyNavigation(-1);
        e.preventDefault();
      }
      else if (e.which === 40) { // Arrow down
        this.onArrowKeyNavigation(1);
        e.preventDefault();
      }
    }
  };

  onButtonClick = () => {
    const isOpen = !this.state.isOpen;
    this.setState({ isOpen });
    this.setFocusOnButton();
  };

  onArrowKeyNavigation = (delta) => {
    const { focusedItemValue, selectedItemValue } = this.state;
    const { items } = this.props;

    const focusedItem = find(items, { value: focusedItemValue });
    const selectedItem = find(items, { value: selectedItemValue });

    let focusedItemIndex = items.length - 1;
    if (focusedItem) {
      focusedItemIndex = items.indexOf(focusedItem);
    }
    else if (selectedItem) {
      focusedItemIndex = items.indexOf(selectedItem);
    }

    let nextFocusedItemIndex = focusedItemIndex + delta;
    if (nextFocusedItemIndex < 0) {
      nextFocusedItemIndex = items.length - 1;
    }
    else if (nextFocusedItemIndex >= items.length) {
      nextFocusedItemIndex = 0;
    }

    this.setState({
      isOpen: true,
      focusedItemValue: items[nextFocusedItemIndex].value,
    }, () => {
      this.scrollToFocusedItem(delta);
    });
  };

  onEnterPress = () => {
    const { focusedItemValue, selectedItemValue } = this.state;
    const { items, onChange = () => {}, dontKeepSelectedOption, dontCloseOnChange } = this.props;

    const focusedItem = find(items, { value: focusedItemValue });
    if (focusedItem) {
      const prevSelectedItem = find(items, { value: selectedItemValue }) || {};
      if (focusedItem.value !== prevSelectedItem.value) {
        onChange(focusedItem, prevSelectedItem);
      }
      const isOpen = Boolean(dontCloseOnChange);
      this.setState({
        isOpen,
        focusedItemValue: null,
        selectedItemValue: dontKeepSelectedOption ? null : focusedItem.value,
      });
    }
  };

  onItemClick = (nextSelectedItem) => {
    const { items, onChange = () => {}, dontKeepSelectedOption, dontCloseOnChange } = this.props;
    const { selectedItemValue } = this.state;

    this.setFocusOnButton();

    const prevSelectedItem = find(items, { value: selectedItemValue }) || {};
    onChange(nextSelectedItem, prevSelectedItem);

    const isOpen = Boolean(dontCloseOnChange);
    this.setState({
      isOpen,
      focusedItemValue: null,
      selectedItemValue: dontKeepSelectedOption ? null : nextSelectedItem.value,
    });
  };

  scrollToFocusedItem = (delta) => {
    const focusedItem = this.focusedDropdownItem;
    const container = focusedItem.parentNode;
    const isWithinViewport = focusedItem.offsetTop >= container.scrollTop
      && focusedItem.offsetTop + focusedItem.offsetHeight <= container.scrollTop + container.offsetHeight;

    if (!isWithinViewport) {
      if (delta === 1) {
        container.scrollTop = focusedItem.offsetTop + focusedItem.offsetHeight - container.offsetHeight;
      }
      else if (delta === -1) {
        container.scrollTop = focusedItem.offsetTop;
      }
    }
  };

  resizeDropdownToFitViewport = () => {
    const dropdown = this.dropdown.current;
    if (dropdown) {
      setTimeout(() => {
        dropdown.style.height = 'auto';
        const availableDropdownYSpace = window.innerHeight - dropdown.getBoundingClientRect().top;
        const dropdownHeight = dropdown.offsetHeight;
        if (availableDropdownYSpace < dropdownHeight) {
          dropdown.style.height = (availableDropdownYSpace - 20) + 'px';
        }
      }, 0);
    }
  };

  render(){
    const {
      className,
      items,
      children,
      overrideClassNames,
      renderItem,
      renderListTop,
      buttonAppearance,
      onClick,
    } = this.props;

    const {
      focusedItemValue,
      selectedItemValue,
      isOpen,
      isFocused,
      isContainerFocusable,
    } = this.state;

    const styles = mergeClassNames(originalStyles, overrideClassNames);

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerFocused]: isFocused,
        [styles.containerButtonAppearance]: buttonAppearance,
      })}
        onClick={onClick}
        onFocus={this.onComponentFocus}
        onBlur={this.onComponentBlur}
        onKeyDown={this.onComponentKeydown}
        ref={this.container}
        tabIndex={ isContainerFocusable ? 0 : -1 }>

        <div className={styles.button}
          ref={this.button}
          onClick={this.onButtonClick}>
          {children}
          <i className={cn(styles.buttonArrow, 'icon-chevron-down')}/>
        </div>

        <ul ref={this.dropdown} className={cn({
          [styles.list]: true,
          [styles.listOpen]: this.props.isOpen || isOpen,
        })}>
          {Boolean(renderListTop) && renderListTop(items)}
          {items.map(item => (
            <li className={cn({
              [styles.item]: true,
              [styles.itemFocused]: item.value === focusedItemValue,
              [styles.itemSelected]: item.value === selectedItemValue,
              [styles.itemDividerAbove]: item.dividerAbove,
              [styles.itemDividerBelow]: item.dividerBelow,
            })}
              onClick={() => this.onItemClick(item)}
              onMouseOver={() => this.setState({ focusedItemValue: item.value })}
              key={item.value}
              ref={element => {
                if (item.value === focusedItemValue) { this.focusedDropdownItem = element; }
              }}>
              {renderItem ? renderItem(item) : item.title}
            </li>
          ))}
        </ul>

      </div>
    );
  }
}
